import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Dropdown,
  Field,
  Option,
  makeStyles,
  tokens,
  Tooltip,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import { Dismiss24Regular, LinkRegular, LinkDismissRegular } from '@fluentui/react-icons';
import { useContext, useMemo, useState } from 'react';

import { TInlineBlock, TInlineBlockDraft, TInlineBlockVersion } from '../../types';
import { SignInContext } from '@modules/users';
import { useSetInlineBlockTag, useSetTaggedBlockForFolder, useGetTagUsage } from '../../api';
import { TPermissions, AccessType } from '@modules/access';
import { ConfirmDialog, TConfirmDialogState, ButtonLoading } from '@shared/components';
import { DEFAULT_OPTION_LABEL, TERMINOLOGY } from '@shared/i18n';
import { filterTagsForDisplay } from '@shared/utils';

const useStyles = makeStyles({
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    maxHeight: '70vh',
    overflowY: 'auto',
  },
  fitContent: {
    width: 'fit-content',
  },
});

export type TUrlConfig = {
  showUrlField: boolean;
  isEdit: boolean;
  handleAddUrl: () => void;
  handleDeleteUrl: () => void;
};

type TProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  inlineBlock: TInlineBlock;
  version: TInlineBlockDraft | TInlineBlockVersion | undefined;
  permissions: TPermissions;
  urlConfig: TUrlConfig;
};

const InlineBlockConfigDialog = ({ isOpen, setIsOpen, inlineBlock, version, permissions, urlConfig }: TProps) => {
  const classes = useStyles();
  const { organization } = useContext(SignInContext);
  const setTagMutation = useSetInlineBlockTag();
  const setForFolderMutation = useSetTaggedBlockForFolder();
  const getTagUsage = useGetTagUsage();
  const [confirmState, setConfirmState] = useState<TConfirmDialogState | undefined>(undefined);

  // Show active tags + the currently assigned tag (even if inactive) so user can see/clear it
  const tagsToDisplay = useMemo(
    () => filterTagsForDisplay(organization.blockTags ?? [], inlineBlock.blockTagId),
    [organization.blockTags, inlineBlock.blockTagId]
  );

  const hasCompletedVersion = inlineBlock.versions.length > 0;
  const canEdit = permissions.accessRight >= AccessType.EDIT;
  const hasUrl = !!version?.url;
  const isCurrentTagDeactivated = Boolean(
    inlineBlock.blockTagId && !tagsToDisplay.find((t) => t.id === inlineBlock.blockTagId)?.active
  );

  const handleTagChange = async (tagId: string | null) => {
    // If the tag is being changed or removed, check for affected documents first
    if (inlineBlock.blockTagId && inlineBlock.blockTagId !== tagId) {
      let taggedSnippetCount = 0;
      try {
        const result = await getTagUsage.mutateAsync(inlineBlock._id);
        taggedSnippetCount = result.taggedSnippetCount;
      } catch {
        // Error is handled by mutation's onError - abort the change
        return;
      }

      // No documents affected - proceed directly without confirmation
      if (taggedSnippetCount === 0) {
        setTagMutation.mutate({ id: inlineBlock._id, blockTagId: tagId });
        return;
      }

      const tagLabel = tagsToDisplay.find((t) => t.id === inlineBlock.blockTagId)?.label ?? inlineBlock.blockTagId;

      const state: TConfirmDialogState = {
        isOpen: true,
        title: `Change tag "${tagLabel}"?`,
        content: (
          <div className="space-y-3">
            <MessageBar intent="warning">
              <MessageBarBody>
                Changing this tag may affect documents using this {TERMINOLOGY.inlineBlock.lower}.
              </MessageBarBody>
            </MessageBar>
            <p>
              This {TERMINOLOGY.inlineBlock.lower} is currently selected in <strong>{taggedSnippetCount}</strong>{' '}
              document{taggedSnippetCount === 1 ? '' : 's'} (across all products, including archived). Changing or
              removing the tag will require manual updates in those documents.
            </p>
          </div>
        ),
        buttonLabel: 'Change tag',
        danger: true,
        onConfirm: async () => {
          try {
            await setTagMutation.mutateAsync({ id: inlineBlock._id, blockTagId: tagId });
            setConfirmState({ ...state, isOpen: false });
          } catch {
            // Error is handled by mutation's onError - close confirm dialog
            setConfirmState({ ...state, isOpen: false });
          }
        },
      };
      setConfirmState(state);
      return;
    }

    setTagMutation.mutate({ id: inlineBlock._id, blockTagId: tagId });
  };

  const handleSetForAll = () => {
    const tagLabel = tagsToDisplay.find((t) => t.id === inlineBlock.blockTagId)?.label ?? inlineBlock.blockTagId;

    const state: TConfirmDialogState = {
      isOpen: true,
      title: 'Set for all documents?',
      content: (
        <div className="space-y-3">
          <MessageBar intent="warning">
            <MessageBarBody>This will override existing selections for documents in this product.</MessageBarBody>
          </MessageBar>
          <p>
            This {TERMINOLOGY.inlineBlock.lower} will be set as the tagged {TERMINOLOGY.inlineBlock.lower} for all
            documents in this product for the &ldquo;{tagLabel}&rdquo; tag, overriding any existing selections.
          </p>
        </div>
      ),
      buttonLabel: 'Set for all documents',
      onConfirm: async () => {
        try {
          await setForFolderMutation.mutateAsync({
            folderId: inlineBlock.resolvedPath[0]._id,
            blockTagId: inlineBlock.blockTagId!,
            blockId: inlineBlock._id,
          });
          setConfirmState({ ...state, isOpen: false });
        } catch {
          setConfirmState({ ...state, isOpen: false });
        }
      },
    };
    setConfirmState(state);
  };

  if (tagsToDisplay.length === 0 && !canEdit) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(_, data) => setIsOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle
              action={
                <DialogTrigger action="close">
                  <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} />
                </DialogTrigger>
              }
            >
              {TERMINOLOGY.inlineBlock.upper} settings
            </DialogTitle>

            <DialogContent className={classes.dialogContent}>
              {canEdit && (
                <Field label="URL">
                  <Tooltip
                    content={`${inlineBlock.draft ? 'Edit' : 'Create'} a draft to manage the URL.`}
                    relationship="description"
                    withArrow
                    visible={urlConfig.isEdit ? false : undefined}
                  >
                    {hasUrl || urlConfig.showUrlField ? (
                      <Button
                        className={classes.fitContent}
                        icon={<LinkDismissRegular />}
                        disabled={!urlConfig.isEdit}
                        onClick={() => {
                          urlConfig.handleDeleteUrl();
                          setIsOpen(false);
                        }}
                      >
                        Remove URL
                      </Button>
                    ) : (
                      <Button
                        className={classes.fitContent}
                        icon={<LinkRegular />}
                        disabled={!urlConfig.isEdit}
                        onClick={() => {
                          urlConfig.handleAddUrl();
                          setIsOpen(false);
                        }}
                      >
                        Add URL
                      </Button>
                    )}
                  </Tooltip>
                </Field>
              )}

              {tagsToDisplay.length > 0 && (
                <Tooltip
                  content="Complete a version before assigning a tag."
                  relationship="description"
                  withArrow
                  visible={!hasCompletedVersion ? undefined : false}
                >
                  <Field
                    label="Tag"
                    validationState={isCurrentTagDeactivated ? 'warning' : undefined}
                    validationMessage={isCurrentTagDeactivated ? 'This tag has been deactivated.' : undefined}
                  >
                    <div className="flex items-center gap-2">
                      <Dropdown
                        className="flex-1"
                        value={(() => {
                          const tag = tagsToDisplay.find((t) => t.id === inlineBlock.blockTagId);
                          if (!tag) return '';
                          return tag.label;
                        })()}
                        selectedOptions={[inlineBlock.blockTagId ?? '']}
                        onOptionSelect={(_, data) => {
                          const selected = data.optionValue;
                          handleTagChange(selected === '' ? null : (selected ?? null));
                        }}
                        disabled={!canEdit || !hasCompletedVersion || getTagUsage.isPending}
                        placeholder={DEFAULT_OPTION_LABEL}
                      >
                        <Option value="" text={DEFAULT_OPTION_LABEL}>
                          {DEFAULT_OPTION_LABEL}
                        </Option>
                        {tagsToDisplay.map((tag) => (
                          <Option key={tag.id} value={tag.id} text={tag.label} disabled={!tag.active}>
                            {tag.label}
                          </Option>
                        ))}
                      </Dropdown>
                      {inlineBlock.blockTagId && (
                        <ButtonLoading
                          className={classes.fitContent}
                          appearance="secondary"
                          loading={setForFolderMutation.isPending}
                          disabled={!canEdit || setTagMutation.isPending || setForFolderMutation.isPending}
                          onClick={handleSetForAll}
                        >
                          Set for all
                        </ButtonLoading>
                      )}
                    </div>
                  </Field>
                </Tooltip>
              )}
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      <ConfirmDialog state={confirmState} setState={setConfirmState} />
    </>
  );
};

export default InlineBlockConfigDialog;
